<template>
  <div class="assignment-container">
    <div class="left-column">
      <case-list
        :org-data="orgData"
        :is-sticky="true"
        :show-template-cases="false"
        :show-bottom-toolbar="false"
        :initial-case-id="state.assignment.caseId"
        @previewCase="(caseConf) => previewCase(caseConf)"
      />
      <assignment-sharing-table
        :assignment="state.assignment"
        @update="(students) => updateStudents(students)"
      />
    </div>
    <div class="right-column new-globals">
      <md-content class="md-scrollbar gray-3 bg right-column-content">
        <case-preview
          v-if="state.previewCase"
          :case-conf="state.previewCase"
          :user="user"
          :show-tags="false"
          :is-compact="true"
        />

        <div class="container">
          <div>
            <md-field>
              <label>Assignment Name</label>
              <md-input v-model="state.assignment.name" :maxlength="64" />
            </md-field>

            <date-picker
              ref="startDate"
              v-model="state.startTime"
              :clearable="false"
              @input="(time) => updateStartDate(time)"
            >
              <template v-slot:input>
                <md-field class="date-picker-field">
                  <label>Start Date</label>
                  <md-input
                    :value="startDateFormatted"
                    readonly
                    @click="clickElement('startDate')"
                  />
                </md-field>
              </template>
            </date-picker>
            <date-picker
              ref="startTime"
              v-model="state.startTime"
              default-value="state.startTime"
              :clearable="false"
              type="time"
              :show-second="false"
              use12h
              @input="(time) => updateStartTime(time)"
            >
              <template v-slot:input>
                <md-field>
                  <label>Start Time</label>
                  <md-input
                    :value="startTimeFormatted"
                    readonly
                    @click="clickElement('startTime')"
                  />
                </md-field>
              </template>
            </date-picker>
            <span v-if="error" class="error">{{ error }}</span>
            <md-checkbox
              v-if="!error && state.assignment.status === 'PENDING'"
              v-model="state.assignment.emailParticipants"
              class="email-checkbox"
            >
              Email participants on assignment start
            </md-checkbox>
          </div>

          <div>
            <md-field>
              <label>Number of Attempts</label>
              <md-input
                v-model.number="state.assignment.attemptsCount"
                v-integer-only
                type="number"
                min="1"
              />
            </md-field>
            <date-picker
              ref="endDate"
              v-model="state.endTime"
              :clearable="false"
              @input="(time) => updateEndDate(time)"
            >
              <template v-slot:input>
                <md-field class="date-picker-field">
                  <label>End Date</label>
                  <md-input
                    :value="endDateFormatted"
                    readonly
                    @click="clickElement('endDate')"
                  />
                </md-field>
              </template>
            </date-picker>
            <date-picker
              ref="endTime"
              :value="state.endTime"
              :clearable="false"
              type="time"
              :show-second="false"
              use12h
              @input="(time) => updateEndTime(time)"
            >
              <template v-slot:input>
                <md-field>
                  <label>End Time</label>
                  <md-input
                    :value="endTimeFormatted"
                    readonly
                    @click="clickElement('endTime')"
                  />
                </md-field>
              </template>
            </date-picker>
            <md-button
              class="md-raised md-primary final-button"
              style="text-align: center"
              :disabled="finalizeDisabled"
              @click="finalize()"
            >
              {{ assignment ? "Finalize Editing" : "Finalize Creation" }}
            </md-button>
          </div>
        </div>
      </md-content>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';

import CasePreview from '~lib/common/components/CasePreview';
import CaseList from '~lib/common/components/CaseList';

import AssignmentSharingTable from './AssignmentSharingTable';

export default {
  name: 'assignment-editor',
  components: {
    DatePicker,
    CasePreview,
    CaseList,
    AssignmentSharingTable,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    orgData: {
      type: Object,
      default: () => {},
    },
    assignment: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      state: {
        assignment: {},
        previewCase: null,
        startTime: null,
        endTime: null,
        email: false,
      },
    };
  },
  computed: {
    startDateFormatted() {
      return moment(this.state.assignment.startTime).format('MMMM D, YYYY');
    },
    startTimeFormatted() {
      return moment(this.state.assignment.startTime).format('h:mm A');
    },
    endDateFormatted() {
      return moment(this.state.assignment.endTime).format('MMMM D, YYYY');
    },
    endTimeFormatted() {
      return moment(this.state.assignment.endTime).format('h:mm A');
    },
    finalizeDisabled() {
      const { startTime, endTime } = this.state.assignment;
      return !startTime || !endTime || !!this.error;
    },
    error() {
      const { startTime, endTime } = this.state;
      if (!startTime || !endTime) {
        return 'Start Date and End Date required.';
      }
      if (startTime > endTime) {
        return 'Start Date must precede End Date.';
      }
      if (endTime < new Date()) {
        return 'End Date must be in the future.';
      }
      if (!this.state.assignment.name) {
        return 'Assignment Name is required.';
      }
      if (!this.state.assignment.attemptsCount) {
        return 'Number of Attempts must be positive.';
      }
      return null;
    },
  },
  watch: {
    assignment(assignment) {
      if (!assignment) {
        return;
      }
      if (
        !this.state.assignment ||
        assignment.id !== this.state.assignment.id
      ) {
        this.state.assignment = assignment;
        this.state.startTime = new Date(assignment.startTime);
        this.state.endTime = new Date(assignment.endTime);
      }
    },
    error(e) {
      console.log(e);
    },
  },
  created() {
    const { assignmentId } = this.$router.currentRoute.params;
    if (!assignmentId || assignmentId === 'new') {
      this.state.startTime = new Date();
      this.state.endTime = new Date();
      this.state.endTime.setDate(this.state.endTime.getDate() + 7);

      this.state.assignment = {
        name: 'New Assignment',
        attemptsCount: 1,
        access: {
          read: {
            users: [this.user.user_id],
            groups: null,
          },
          write: {
            users: [this.user.user_id],
            groups: null,
          },
          owner: this.user.user_id,
        },
        students: {
          users: [],
          groups: [],
        },
        status: 'PENDING',
        startTime: this.state.startTime.toJSON(),
        endTime: this.state.endTime.toJSON(),
      };
    } else {
      this.$emit('fetchById', assignmentId);
    }
  },
  methods: {
    previewCase(caseConf) {
      this.state.previewCase = caseConf;
      if (this.caseChangedInExistingAssignment(caseConf.id)) {
        this.state.assignment.previousCaseId  = this.state.assignment.caseId;
      }
      this.state.assignment.caseId = caseConf.id;
      this.state.assignment.caseName = caseConf.name;
    },
    caseChangedInExistingAssignment(newCaseId) {
      return this.state.assignment.id && this.state.assignment.caseId && this.state.assignment.caseId !== newCaseId;
    },
    finalize() {
      this.state.assignment.status = 'PENDING';
      this.$emit('save', this.state.assignment);
      this.$analytics.event('Assignments', 'create_new_assignment');
      setTimeout(() => {
        // This timeout is a workaround to give the save time to happen
        // before the fetch in the parent page
        this.$router.go(-1);
        // this.$router.push({ path: '/assignments' });
      }, 200);
    },
    updateStudents(students) {
      this.state.assignment.students = students;
    },
    updateStartDate(value) {
      const oldDate = new Date(this.state.assignment.startTime);
      const newDate = new Date(value);
      this.state.startTime.setFullYear(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate()
      );
      this.state.startTime.setHours(oldDate.getHours(), oldDate.getMinutes());
      this.state.startTime = new Date(this.state.startTime); // trigger reactivity
      this.state.assignment.startTime = this.state.startTime.toJSON();
    },
    updateStartTime(value) {
      this.state.startTime.setTime(new Date(value).getTime());
      this.state.startTime = new Date(this.state.startTime); // trigger reactivity
      this.state.assignment.startTime = this.state.startTime.toJSON();
    },
    updateEndDate(value) {
      const oldDate = new Date(this.state.assignment.endTime);
      const newDate = new Date(value);
      this.state.endTime.setFullYear(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate()
      );
      this.state.endTime.setHours(oldDate.getHours(), oldDate.getMinutes());
      this.state.endTime = new Date(this.state.endTime); // trigger reactivity
      this.state.assignment.endTime = this.state.endTime.toJSON();
    },
    updateEndTime(value) {
      this.state.endTime.setTime(new Date(value).getTime());
      this.state.endTime = new Date(this.state.endTime); // trigger reactivity
      this.state.assignment.endTime = this.state.endTime.toJSON();
    },
    clickElement(element) {
      this.$refs[element].openPopup();
    },
  },
};
</script>

<style scoped>
.assignment-container {
  padding: 20px 25px;
  height: 100%;
  will-change: transform;
  min-width: 500px;
  width: 100%;
  color: #000;
  display: flex;
  justify-content: space-between;
}

.left-column {
  display: flex;
  flex-direction: column;
  flex: 1 1;
}

.left-column > * {
  max-height: 50%;
}

.right-column {
  padding-left: 25px;
  flex: 0 0;
}
.right-column > :not(:last-child) {
  margin-bottom: 8px;
}

.right-column-content {
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: 100%;
}

.container {
  display: flex;
  flex-direction: row;
}
.container > :not(:last-child) {
  margin-right: 8px;
}

.final-button {
  width: 160px;
  float: right;
}

.error {
  color: red;
}

.assignment-container >>> .vmdtp_text {
  overflow: visible;
}
.assignment-container >>> .vmdtp_arrow {
  line-height: 24px;
}
.assignment-container >>> .vmdtp_input {
  display: none;
}

.email-checkbox {
  margin-top: 6px;
}

.date-picker-field {
  margin-top: 0;
  margin-bottom: 20px;
}
</style>